/**
 * CP-3.3 — Marketplace search (leads & helpers).
 *
 * Lists contractors that the viewer is allowed to discover, with admin-configurable
 * weighted ranking. Vacation contractors stay visible but are clearly badged.
 * Cards link to the public contractor profile (/app/contractors/:uuid).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MarketplaceRatingBadge } from "@/pages/Phase6";

function FilterRail({ value, onChange, catalogs }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <aside className="avcp-card p-5 space-y-3">
      <h3 className="font-semibold">Filters</h3>
      <label className="grid gap-1 text-sm">
        <span>Region</span>
        <select className="avcp-input" data-testid={TID.mpFilterRegion}
                value={value.region_code || ""}
                onChange={(e) => set("region_code", e.target.value)}>
          <option value="">Any</option>
          {catalogs.regions.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
      </label>
      <label className="grid gap-1 text-sm">
        <span>Skill (single)</span>
        <select className="avcp-input" data-testid={TID.mpFilterSkill}
                value={value.skill_codes || ""}
                onChange={(e) => set("skill_codes", e.target.value)}>
          <option value="">Any</option>
          {catalogs.skills.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
      </label>
      <label className="grid gap-1 text-sm">
        <span>Language</span>
        <select className="avcp-input" data-testid={TID.mpFilterLang}
                value={value.language_codes || ""}
                onChange={(e) => set("language_codes", e.target.value)}>
          <option value="">Any</option>
          {catalogs.languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </label>
      <label className="grid gap-1 text-sm">
        <span>Min profile completeness</span>
        <input type="number" min="0" max="100" className="avcp-input"
               data-testid={TID.mpFilterCompleteness}
               value={value.min_completeness || ""}
               onChange={(e) => set("min_completeness", e.target.value)} />
      </label>
      <label className="grid gap-1 text-sm">
        <span>Available from</span>
        <input type="datetime-local" className="avcp-input" data-testid={TID.mpFilterFrom}
               value={value.availability_from || ""}
               onChange={(e) => set("availability_from", e.target.value)} />
      </label>
      <label className="grid gap-1 text-sm">
        <span>Available to</span>
        <input type="datetime-local" className="avcp-input" data-testid={TID.mpFilterTo}
               value={value.availability_to || ""}
               onChange={(e) => set("availability_to", e.target.value)} />
      </label>
    </aside>
  );
}

function Card({ c, isAdmin }) {
  const vacation = c.operational_status === "vacation";
  return (
    <div className={`avcp-card p-5 ${vacation ? "border-amber-300 border" : ""}`}
         data-testid={TID.mpCard}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold leading-tight">
            {c.company_name || "(unnamed contractor)"}
          </h3>
          <div className="text-xs text-[var(--avcp-ink-soft)] capitalize">
            {c.crew_capability_kind || "—"} · {c.years_experience || "?"} yrs · {c.timezone || "UTC"}
          </div>
        </div>
        {vacation && (
          <span className="avcp-pill warn" data-testid={TID.mpCardVacation}>On vacation</span>
        )}
      </div>
      {/* CP-6.3 — rating aggregate badge (always rendered; degrades gracefully) */}
      <div className="mt-2">
        <MarketplaceRatingBadge ratingAvg={c.rating_avg} ratingCount={c.rating_count} />
      </div>
      {c.short_bio && <p className="text-sm mt-3">{c.short_bio}</p>}
      <div className="flex items-center justify-between mt-4 text-sm">
        <span data-testid={TID.mpScore}>
          Score <span className="font-mono">{c.ranking_score}</span> · Completeness <span className="font-mono">{c.completeness_score}%</span>
        </span>
        <Link to={`/app/contractors/${c.user_uuid}`} className="avcp-btn ghost"
              data-testid={TID.mpOpenProfile}>View profile</Link>
      </div>
      {isAdmin && c.ranking_factors && (
        <details className="mt-3" data-testid={TID.mpExplain}>
          <summary className="text-xs cursor-pointer text-[var(--avcp-ink-soft)]">Ranking factors</summary>
          <ul className="text-xs font-mono mt-2 space-y-0.5">
            {Object.entries(c.ranking_factors).map(([k, v]) => (
              <li key={k} className="flex justify-between"><span>{k}</span><span>{v}</span></li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function MarketplaceBase({ kind }) {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("super_admin");
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [catalogs, setCatalogs] = useState({ skills: [], regions: [], languages: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, r, l] = await Promise.all([
          api("/catalog/skills"), api("/catalog/regions"), api("/catalog/languages"),
        ]);
        setCatalogs({ skills: s.data || [], regions: r.data || [], languages: l.data || [] });
      } catch (e) { toast.error(e.message); }
    })();
  }, []);

  const fetchResults = async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v != null) qs.set(k, v);
    });
    if (isAdmin) qs.set("explain", "true");
    try {
      const r = await api(`/marketplace/${kind}?${qs.toString()}`);
      setResults(r.data || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { fetchResults(); }, []);

  const title = kind === "leads" ? "Lead Technician Marketplace" : "Helper Technician Marketplace";

  return (
    <Layout>
      <div data-testid={TID.mpRoot}>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
            <p className="text-[var(--avcp-ink-soft)] mt-1">
              Independent technicians on Workforce Partners. Rates, availability, and acceptance
              are entirely controlled by each technician.
            </p>
          </div>
          <button className="avcp-btn primary" data-testid={TID.mpApply}
                  disabled={loading} onClick={fetchResults}>
            {loading ? "Loading…" : "Apply filters"}
          </button>
        </div>
        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          <FilterRail value={filters} onChange={setFilters} catalogs={catalogs} />
          <div className="grid sm:grid-cols-2 gap-4" data-testid={TID.mpResults}>
            {results.length === 0 && !loading && (
              <div className="avcp-card p-6 sm:col-span-2 text-sm text-[var(--avcp-ink-soft)]">
                No contractors match these filters.
              </div>
            )}
            {results.map((c) => <Card key={c.user_uuid} c={c} isAdmin={isAdmin} />)}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export const MarketplaceLeads = () => <MarketplaceBase kind="leads" />;
export const MarketplaceHelpers = () => <MarketplaceBase kind="helpers" />;

export function PublicContractorProfile() {
  const { uuid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api(`/contractors/${uuid}`);
        setData(r);
      } catch (e) { setError(e.message); }
    })();
  }, [uuid]);
  if (error) return <Layout><div className="avcp-card p-6 text-rose-700">{error}</div></Layout>;
  if (!data) return <Layout><div className="avcp-card p-6">Loading…</div></Layout>;
  const p = data.profile || {};
  const canBook = user?.roles?.includes("client");
  return (
    <Layout>
      <div data-testid={TID.publicProfileRoot}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{p.company_name || "(unnamed contractor)"}</h1>
            <p className="text-[var(--avcp-ink-soft)] mt-1">{p.short_bio || ""}</p>
          </div>
          {canBook && (
            <Link to={`/app/contractors/${uuid}/book`} className="avcp-btn primary"
                  data-testid={TID.bookOpenWizard}>
              Book this Lead Technician
            </Link>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-6">
          <div className="avcp-card p-5 md:col-span-2">
            <h2 className="font-semibold mb-2">About</h2>
            <p className="text-sm whitespace-pre-line">{p.long_bio || "—"}</p>
            {p.website && (
              <p className="text-sm mt-3">Website: <a className="underline" href={p.website} target="_blank" rel="noreferrer">{p.website}</a></p>
            )}
            <div className="mt-4 text-sm grid grid-cols-2 gap-2">
              <div><span className="text-[var(--avcp-ink-soft)]">Years</span>: {p.years_experience ?? "—"}</div>
              <div><span className="text-[var(--avcp-ink-soft)]">Crew</span>: {p.crew_capability_kind || "—"}</div>
              <div><span className="text-[var(--avcp-ink-soft)]">Travel</span>: {p.travel_radius_kind || "—"}</div>
              <div><span className="text-[var(--avcp-ink-soft)]">Status</span>: {p.operational_status || "—"}</div>
            </div>
          </div>
          <div className="space-y-5">
            <div className="avcp-card p-5">
              <h2 className="font-semibold mb-2">Skills</h2>
              <ul className="space-y-1 text-sm">
                {data.skills.map((s) => <li key={s.code}>{s.label} <span className="text-xs text-[var(--avcp-ink-soft)]">· {s.proficiency}</span></li>)}
                {data.skills.length === 0 && <li className="text-[var(--avcp-ink-soft)]">—</li>}
              </ul>
            </div>
            <div className="avcp-card p-5">
              <h2 className="font-semibold mb-2">Service regions</h2>
              <ul className="space-y-1 text-sm">
                {data.regions.map((r) => <li key={r.code}>{r.label}</li>)}
                {data.regions.length === 0 && <li className="text-[var(--avcp-ink-soft)]">—</li>}
              </ul>
            </div>
            <div className="avcp-card p-5">
              <h2 className="font-semibold mb-2">Certifications</h2>
              <ul className="space-y-1 text-sm">
                {data.certifications.map((c) => <li key={c.code}>{c.label}{c.issuer ? <span className="text-xs text-[var(--avcp-ink-soft)]"> · {c.issuer}</span> : null}</li>)}
                {data.certifications.length === 0 && <li className="text-[var(--avcp-ink-soft)]">—</li>}
              </ul>
            </div>
            <div className="avcp-card p-5">
              <h2 className="font-semibold mb-2">Languages</h2>
              <ul className="space-y-1 text-sm">
                {data.languages.map((l) => <li key={l.code}>{l.label} <span className="text-xs text-[var(--avcp-ink-soft)]">· {l.proficiency}</span></li>)}
                {data.languages.length === 0 && <li className="text-[var(--avcp-ink-soft)]">—</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
